// What the panel shows when something on the board is selected (Cal-02…11):
// a booking, a rider trip, a person's day, an hour, and the Assign view.
// Each opens IN the panel with Back, like the live map's staff details
// (LiveMapStaffDetail) — nothing covers the timeline.
//
// ⚠ DATA WALL: no view here names a client. The booking facts are the
// therapist, hub, booking type and ref; "Open booking" is where client details
// live, under that page's own per-role rule.

import { ArrowLeftIcon, ArrowRightIcon, MapTrifoldIcon } from "@phosphor-icons/react";
import { Button } from "@bednbody/ui/components/button";
import { cn } from "@bednbody/ui/lib/utils";
import type { ReactNode } from "react";
import { ListCard, ListRow, StatusPill, TONE_DOT, type Tone } from "../live-map/LiveMapCards";
import {
  type BoardRow,
  clockLabel,
  type LegBlock,
  type MinuteSpan,
  type SessionBlock,
  serviceLabel,
  spanLabel,
} from "./calendar-board";
import type { HourDetail } from "./calendar-board.hours";
import {
  bookingKindLabel,
  LEG_LABEL,
  PHASE_LABEL,
  PHASE_TONE,
  personStatusLabel,
  personStatusTone,
} from "./calendar-board.labels";
import { BADGE, BadgeImage, legPlaceUrl, StaffAvatar } from "./CalendarIcons";
import { CalendarShiftEditor } from "./CalendarShiftEditor";

const LEG_TONE: Record<LegBlock["kind"], Tone> = {
  drop_off: "warning",
  pickup: "info",
  return: "muted",
};

export function Dot({ tone }: { tone: Tone }) {
  return (
    <span className="flex w-4 shrink-0 justify-center">
      <span className={cn("size-2 rounded-full", TONE_DOT[tone])} />
    </span>
  );
}

function Facts({ items }: { items: readonly { label: string; value: string; warn?: boolean }[] }) {
  return (
    <dl className="divide-y divide-border rounded-lg bg-muted/40 px-3">
      {items.map((fact) => (
        <div className="flex items-center justify-between gap-3 py-2 text-sm" key={fact.label}>
          <dt className="text-muted-foreground">{fact.label}</dt>
          <dd
            className={cn(
              "truncate text-right font-semibold",
              fact.warn ? "text-warning" : "text-foreground",
            )}
          >
            {fact.value}
          </dd>
        </div>
      ))}
    </dl>
  );
}

export function ListHead({ children }: { children: ReactNode }) {
  return <h4 className="px-1 text-xs font-bold text-muted-foreground">{children}</h4>;
}

/** The frame every detail shares: Back and the state pill, the heading, then the body. */
export function DetailFrame({
  onBack,
  pill,
  title,
  subtitle,
  meta,
  children,
  actions,
  leading,
}: {
  onBack: () => void;
  pill: { label: string; tone: Tone };
  title: string;
  subtitle: string;
  meta: string;
  children: ReactNode;
  actions?: ReactNode;
  /** A face or picture beside the heading. */
  leading?: ReactNode;
}) {
  return (
    <section aria-label={title} className="flex min-h-0 flex-1 flex-col">
      {/* A real bar with a bordered button, not a grey word in a corner:
          Leon couldn't find the way back (2026-10-23). Esc does the same. */}
      <div className="flex items-center gap-2 border-b border-border bg-muted/50 p-2">
        <Button className="bg-card shadow-xs" onClick={onBack} variant="outline">
          <ArrowLeftIcon data-icon="inline-start" />
          Back to list
        </Button>
        <span className="ml-auto">
          <StatusPill label={pill.label} tone={pill.tone} />
        </span>
      </div>
      <div className="min-h-0 flex-1 space-y-4 overflow-y-auto p-4">
        <div className="flex items-center gap-3">
          {leading}
          <div className="min-w-0">
            <h3 className="text-lg font-bold leading-tight text-foreground">{title}</h3>
            <p className="text-sm font-semibold text-foreground">{subtitle}</p>
            <p className="text-xs text-muted-foreground">{meta}</p>
          </div>
        </div>
        {children}
      </div>
      {actions ? <div className="flex gap-2 border-t border-border p-3">{actions}</div> : null}
    </section>
  );
}

// ── booking (Cal-05, Cal-06) ────────────────────────────────────────────────

export function BookingDetail({
  session,
  therapist,
  hubName,
  legs,
  riderName,
  onBack,
  onOpenLeg,
  onOpenBooking,
  onShowOnMap,
}: {
  session: SessionBlock;
  therapist: string;
  hubName: string;
  legs: readonly LegBlock[];
  riderName: (riderId: string) => string;
  onBack: () => void;
  onOpenLeg: (leg: LegBlock) => void;
  onOpenBooking: () => void;
  onShowOnMap: () => void;
}) {
  return (
    <DetailFrame
      actions={
        <>
          <Button className="flex-1" onClick={onOpenBooking}>
            Open booking
            <ArrowRightIcon data-icon="inline-end" />
          </Button>
          <Button onClick={onShowOnMap} variant="outline">
            <MapTrifoldIcon data-icon="inline-start" />
            On map
          </Button>
        </>
      }
      meta={`Booking ${session.ref}`}
      onBack={onBack}
      pill={{ label: PHASE_LABEL[session.phase], tone: PHASE_TONE[session.phase] }}
      subtitle={spanLabel(session)}
      title={serviceLabel(session)}
    >
      <Facts
        items={[
          { label: "Therapist", value: therapist },
          { label: "Hub", value: hubName },
          { label: "Booking type", value: bookingKindLabel(session.bookingKind) },
        ]}
      />
      <div className="space-y-1">
        <ListHead>Rider trips</ListHead>
        {legs.length === 0 ? (
          <p className="px-1 text-sm text-muted-foreground">No rider trips planned yet.</p>
        ) : (
          <ListCard>
            {legs.map((leg) => (
              <ListRow
                key={leg.id}
                leading={<BadgeImage size="md" src={legPlaceUrl(leg.kind)} />}
                onSelect={() => onOpenLeg(leg)}
                subtitle={spanLabel(leg)}
                title={`${LEG_LABEL[leg.kind]} · ${riderName(leg.riderId)}`}
              />
            ))}
          </ListCard>
        )}
      </div>
    </DetailFrame>
  );
}

// ── rider trip (Cal-07) ─────────────────────────────────────────────────────

export function TripDetail({
  leg,
  rider,
  session,
  therapist,
  legs,
  onBack,
  onOpenLeg,
  onOpenSession,
  onShowOnMap,
}: {
  leg: LegBlock;
  rider: BoardRow | null;
  session: SessionBlock | null;
  therapist: string;
  legs: readonly LegBlock[];
  onBack: () => void;
  onOpenLeg: (leg: LegBlock) => void;
  onOpenSession: () => void;
  onShowOnMap: () => void;
}) {
  const riderName = rider?.name ?? "Rider";
  return (
    <DetailFrame
      actions={
        <>
          <Button className="flex-1" disabled={!session} onClick={onOpenSession}>
            Open booking
            <ArrowRightIcon data-icon="inline-end" />
          </Button>
          <Button onClick={onShowOnMap} variant="outline">
            <MapTrifoldIcon data-icon="inline-start" />
            On map
          </Button>
        </>
      }
      leading={rider ? <StaffAvatar role="rider" size="lg" status={rider.status} /> : null}
      meta={`For booking ${leg.ref}`}
      onBack={onBack}
      pill={{ label: LEG_LABEL[leg.kind], tone: LEG_TONE[leg.kind] }}
      subtitle={spanLabel(leg)}
      title={`${LEG_LABEL[leg.kind]} · ${riderName}`}
    >
      <Facts
        items={[
          { label: "Rider", value: riderName },
          { label: "Hub", value: rider?.hubName ?? "—" },
          { label: "Session", value: session ? `${serviceLabel(session)} · ${therapist}` : "—" },
          { label: "Session time", value: session ? spanLabel(session) : "—" },
        ]}
      />
      <div className="space-y-1">
        <ListHead>All trips for this booking</ListHead>
        <ListCard>
          {legs.map((other) => (
            <ListRow
              key={other.id}
              leading={<BadgeImage size="md" src={legPlaceUrl(other.kind)} />}
              onSelect={() => onOpenLeg(other)}
              selected={other.id === leg.id}
              subtitle={spanLabel(other)}
              title={`${LEG_LABEL[other.kind]} · ${riderName}`}
            />
          ))}
        </ListCard>
      </div>
    </DetailFrame>
  );
}

// ── a person's day, and editing their shift (Cal-08, 09, 10) ────────────────

export function PersonDetail({
  row,
  nowMin,
  isToday,
  editing,
  editLabel = "Edit shift",
  onEdit,
  onCancelEdit,
  onSaveShift,
  onBack,
  onOpenBlock,
  onShowOnMap,
}: {
  row: BoardRow;
  nowMin: number;
  isToday: boolean;
  editing: boolean;
  /** Read-only calendars send the edit to the Schedules page instead. */
  editLabel?: string;
  onEdit: () => void;
  onCancelEdit: () => void;
  onSaveShift: (shift: MinuteSpan | null) => void;
  onBack: () => void;
  onOpenBlock: (block: SessionBlock | LegBlock) => void;
  onShowOnMap: () => void;
}) {
  const late = row.status.kind === "not_clocked_in";
  const clockIn = row.dayOff
    ? "Day off"
    : !isToday
      ? "—"
      : row.clockedIn
        ? "Clocked in"
        : row.shift && row.shift.startMin <= nowMin
          ? "Not clocked in"
          : `Shift starts ${clockLabel(row.shift?.startMin ?? 0)}`;
  const isRider = row.role === "rider";
  const pill = { label: personStatusLabel(row.status), tone: personStatusTone(row.status) };
  const title = row.name;
  const subtitle = row.shift ? `Shift ${spanLabel(row.shift)}` : "Day off";
  const meta = `${row.hubName} hub · ${isRider ? "Rider" : "Therapist"}`;
  const avatar = <StaffAvatar role={row.role} size="lg" status={row.status} />;

  if (editing) {
    return (
      <DetailFrame
        leading={avatar}
        meta={meta}
        onBack={onCancelEdit}
        pill={pill}
        subtitle={subtitle}
        title={title}
      >
        <CalendarShiftEditor onCancel={onCancelEdit} onSave={onSaveShift} row={row} />
      </DetailFrame>
    );
  }

  return (
    <DetailFrame
      actions={
        <>
          <Button className="flex-1" onClick={onEdit}>
            {editLabel}
          </Button>
          <Button onClick={onShowOnMap} variant="outline">
            <MapTrifoldIcon data-icon="inline-start" />
            On map
          </Button>
        </>
      }
      leading={avatar}
      meta={meta}
      onBack={onBack}
      pill={pill}
      subtitle={subtitle}
      title={title}
    >
      <Facts
        items={[
          { label: "Clock-in", value: clockIn, warn: late },
          { label: isRider ? "Trips today" : "Sessions today", value: String(row.blocks.length) },
        ]}
      />
      {!isRider && row.skills.length > 0 ? (
        <ul aria-label="Qualified for" className="flex flex-wrap gap-1.5">
          {row.skills.map((skill) => (
            <li key={skill}>
              <StatusPill label={skill} tone="success" />
            </li>
          ))}
        </ul>
      ) : null}
      <div className="space-y-1">
        <ListHead>{isRider ? "Trips today" : "Sessions today"}</ListHead>
        {row.blocks.length === 0 ? (
          <p className="px-1 text-sm text-muted-foreground">Nothing booked today.</p>
        ) : (
          <ListCard>
            {row.blocks.map((block) => (
              <ListRow
                key={block.id}
                leading={
                  block.kind === "session" ? (
                    <Dot tone={PHASE_TONE[block.phase]} />
                  ) : (
                    <BadgeImage size="md" src={legPlaceUrl(block.kind)} />
                  )
                }
                onSelect={() => onOpenBlock(block)}
                subtitle={
                  block.kind === "session" ? `${spanLabel(block)} · ${block.ref}` : spanLabel(block)
                }
                title={
                  block.kind === "session"
                    ? serviceLabel(block)
                    : `${LEG_LABEL[block.kind]} · ${block.ref}`
                }
              />
            ))}
          </ListCard>
        )}
      </div>
    </DetailFrame>
  );
}

// ── an hour of the strip (Cal-11) ───────────────────────────────────────────

export function HourDetailView({
  detail,
  scopeLabel,
  hubName,
  onBack,
  onOpenPerson,
  onAssign,
}: {
  detail: HourDetail;
  scopeLabel: string;
  hubName: (hubId: string) => string;
  onBack: () => void;
  onOpenPerson: (staffId: string) => void;
  onAssign: (bookingId: string) => void;
}) {
  const { cell } = detail;
  return (
    <DetailFrame
      meta={scopeLabel}
      onBack={onBack}
      pill={{
        label: cell.free === 0 ? "Nobody free" : `${cell.free} free`,
        tone: cell.free === 0 ? "warning" : "success",
      }}
      subtitle={`${cell.onDuty} therapist${cell.onDuty === 1 ? "" : "s"} on duty · ${cell.free} free`}
      title={spanLabel({ startMin: cell.startMin, endMin: cell.startMin + 60 })}
    >
      {detail.waiting.length > 0 ? (
        <div className="space-y-1">
          <ListHead>Waiting for a therapist</ListHead>
          <ListCard>
            {detail.waiting.map((session) => (
              <ListRow
                key={session.id}
                leading={<BadgeImage size="md" src={BADGE.needsTherapist} />}
                onSelect={() => onAssign(session.bookingId)}
                subtitle={`${spanLabel(session)} · ${hubName(session.hubId)}`}
                title={`${serviceLabel(session)} · no therapist`}
                trailing={<StatusPill label="Assign" tone="danger" />}
              />
            ))}
          </ListCard>
        </div>
      ) : null}
      <div className="space-y-1">
        <ListHead>On duty</ListHead>
        {detail.people.length === 0 ? (
          <p className="px-1 text-sm text-muted-foreground">No therapist is on duty this hour.</p>
        ) : (
          <ListCard>
            {detail.people.map(({ row, session }) => (
              <ListRow
                key={row.staffId}
                leading={<StaffAvatar role={row.role} status={row.status} />}
                onSelect={() => onOpenPerson(row.staffId)}
                subtitle={
                  session
                    ? `Busy · ${serviceLabel(session)} until ${clockLabel(session.endMin)}`
                    : row.clockedIn
                      ? "Free"
                      : "Free · not clocked in"
                }
                title={row.name}
              />
            ))}
          </ListCard>
        )}
      </div>
    </DetailFrame>
  );
}
